import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Badge, Button, Card, EngineKeyline } from '@/components/ui/primitives';
import { ErrorState, LoadingState } from '@/components/DataState';
import { cn } from '@/lib/utils';
import {
  approveContent, channelSpecs, createTemplate, getBrandKit, getContent, listProviders,
  rejectContent, scheduleContent, studioCaption, studioIdeas, studioVisualBrief, submitReview, updateContent,
} from '@/data/sources-social';
import {
  CONTENT_LAYOUTS, MAX_CAROUSEL_PAGES, STYLE_PRESETS, aspectCss, channelLabel, statusLabel,
} from '@/data/studio-layouts';
import type { Align, Channel, ContentPage, Provider, PublishState, TextSize, VisualBrief } from '@/types/social';
import { ContentPreview, CarouselStrip, isBakedPreview } from './ContentPreview';
import { PublishMirror } from './PublishMirror';
import { PublishControl } from './PublishControl';
import { extractDraftLinkSources } from '@/data/social-publish';
import { mirrorFromDraft } from '@/data/social-mirror';
import { PublishStateChip } from './PublishStateChip';
import { StockMediaPicker } from './StockMediaPicker';
import { BrandKitPanel } from './BrandKitPanel';
import { useStudioAutosave, type AutosaveStatus, type StudioEditableState } from './useStudioAutosave';

type Step = 'concept' | 'copy' | 'media' | 'layout' | 'preview';
const STEPS: Array<{ key: Step; label: string }> = [
  { key: 'concept', label: 'Concept' },
  { key: 'copy', label: 'Copy' },
  { key: 'media', label: 'Media' },
  { key: 'layout', label: 'Layout' },
  { key: 'preview', label: 'Preview' },
];

const inputCls = 'h-control w-full rounded-md border border-border-default bg-canvas px-3 text-sm text-text-primary outline-none focus:border-ai focus:ring-2 focus:ring-ai/30';
const areaCls = 'w-full rounded-md border border-border-default bg-canvas px-3 py-2 text-sm text-text-primary outline-none focus:border-ai focus:ring-2 focus:ring-ai/30';

export function StudioEditorPage() {
  const { id } = useParams();
  const contentId = Number(id);
  const navigate = useNavigate();
  const qc = useQueryClient();

  const itemQ = useQuery({ queryKey: ['social-content', contentId], queryFn: () => getContent(contentId), enabled: Number.isFinite(contentId) });
  const brandQ = useQuery({ queryKey: ['brand-kit'], queryFn: getBrandKit });
  const specsQ = useQuery({ queryKey: ['channel-specs'], queryFn: channelSpecs });
  const providersQ = useQuery({ queryKey: ['social-providers'], queryFn: listProviders });

  const [step, setStep] = useState<Step>('concept');
  const [brief, setBrief] = useState<VisualBrief | null>(null);
  const [title, setTitle] = useState('');
  const [active, setActive] = useState(0);
  const [brandOpen, setBrandOpen] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    if (!itemQ.data) return;
    setTitle(itemQ.data.title);
    setBrief(
      itemQ.data.visualBrief ?? {
        brief: '', stylePreset: '', aspect: 'square', imagePrompt: '', format: 'single',
        pages: [{ idx: 0, layoutKey: itemQ.data.layoutKey || 'hero-media', slots: { logo: true }, align: 'start', textSize: 'm' }],
      },
    );
  }, [itemQ.data]);

  const item = itemQ.data;
  const brand = brandQ.data ?? null;
  const pages = brief?.pages ?? [];
  const page: ContentPage | undefined = pages[active];
  const spec = item && specsQ.data ? specsQ.data[item.channel] : undefined;
  const connectionState: ConnectionState | null = item && providersQ.data
    ? (providersQ.data.providers.find((p) => p.provider === item.channel)?.connectionState ?? null)
    : null;

  const patchPage = (idx: number, fn: (p: ContentPage) => ContentPage) =>
    setBrief((b) => (b ? { ...b, pages: b.pages.map((p, i) => (i === idx ? fn(p) : p)) } : b));
  const setSlot = <K extends keyof ContentPage['slots']>(k: K, v: ContentPage['slots'][K]) =>
    patchPage(active, (p) => ({ ...p, slots: { ...p.slots, [k]: v } }));

  const save = useMutation({
    mutationFn: () => {
      if (!item || !brief) throw new Error('not_loaded');
      const primary = brief.pages[0]?.slots;
      const bodyText = [primary?.headline, primary?.body].filter(Boolean).join('\n');
      return updateContent(item.id, { channel: item.channel, title, bodyText, layoutKey: brief.pages[0]?.layoutKey, visualBrief: brief });
    },
    onSuccess: () => { setNotice('Saved.'); void qc.invalidateQueries({ queryKey: ['social-content'] }); },
  });

  const doAction = useMutation({
    mutationFn: async (a: { kind: 'submit' | 'approve' | 'reject' | 'schedule'; note?: string; when?: string | null }) => {
      if (!item) throw new Error('not_loaded');
      if (a.kind === 'submit') return submitReview(item.id);
      if (a.kind === 'approve') return approveContent(item.id);
      if (a.kind === 'reject') return rejectContent(item.id, a.note ?? '');
      return scheduleContent(item.id, a.when ?? null);
    },
    onSuccess: (updated) => { qc.setQueryData(['social-content', contentId], updated); void qc.invalidateQueries({ queryKey: ['social-content'] }); setNotice('Updated.'); },
  });

  // P0-A autosave: persist the editable composition (debounced) via the SAFE,
  // approval-preserving path + localStorage refresh-recovery + concurrency guard.
  const editableState: StudioEditableState = useMemo(() => {
    const primary = brief?.pages[0]?.slots;
    const bodyText = [primary?.headline, primary?.body].filter(Boolean).join('\n');
    return { title, bodyText, layoutKey: brief?.pages[0]?.layoutKey ?? '', visualBrief: brief };
  }, [title, brief]);
  const autosave = useStudioAutosave({
    contentId,
    enabled: Boolean(itemQ.data && brief),
    state: editableState,
    serverUpdatedAt: itemQ.data?.updatedAt ?? null,
  });
  const applyRecovered = (st: StudioEditableState) => { setTitle(st.title); setBrief(st.visualBrief); };

  // WYSIWYG mirror: what will ACTUALLY publish for this draft's channel, derived
  // from the SAME shared model the publisher uses (data/social-mirror.ts ==
  // SocialPostModel::fromDraft). Reads pages[0] (the publish primary page) so the
  // mirror cannot drift from the post. link/first-comment inputs mirror the BE
  // source-priority via extractDraftLinkSources (draft field, else provenance —
  // SocialPostModel::linkSource/firstCommentSource); a repurpose-class draft
  // whose provenance the FE cannot fully see is flagged (maybeHiddenFirstComment)
  // so the publish confirm can disclose it honestly instead of silence.
  const linkSources = useMemo(() => extractDraftLinkSources(itemQ.data), [itemQ.data]);
  const mirrorPost = useMemo(() => {
    const primary = brief?.pages[0]?.slots;
    const bodyText = [primary?.headline, primary?.body].filter(Boolean).join('\n');
    return mirrorFromDraft({
      channel: itemQ.data?.channel ?? 'generic',
      bodyText,
      media: primary?.media ?? null,
      link: linkSources.link,
      suggestedFirstComment: linkSources.suggestedFirstComment,
    });
  }, [brief, itemQ.data?.channel, linkSources]);
  // B1 save-truth gate input: the SAME serialization the autosave hook compares,
  // so "dirty since confirm" can never disagree with what autosave persists.
  const editableSerialized = useMemo(() => JSON.stringify(editableState), [editableState]);


  if (itemQ.isLoading) return <div className="mx-auto max-w-6xl p-6"><LoadingState rows={5} /></div>;
  if (itemQ.isError || !item || !brief || !page) return <div className="mx-auto max-w-6xl p-6"><ErrorState error={itemQ.error} onRetry={() => void itemQ.refetch()} /></div>;

  const st = statusLabel(item.reviewStatus, item.publishState, item.scheduledFor, item.providerRef);

  return (
    <div className="mx-auto max-w-6xl px-4">
      <div className="flex flex-wrap items-center justify-between gap-3 py-4">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/social/content-studio')} className="text-sm text-text-secondary hover:text-text-primary">&larr; Studio</button>
          <input className={cn(inputCls, 'w-64')} value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Untitled draft" aria-label="Title" />
          <Badge tone={st.tone}>{st.label}</Badge>
        </div>
        <div className="flex items-center gap-2">
          <AutosaveIndicator status={autosave.status} />
          <Button variant="secondary" onClick={() => setBrandOpen(true)}>Brand Kit</Button>
          <Button variant="ai" onClick={() => save.mutate()} disabled={save.isPending}>{save.isPending ? 'Saving…' : 'Save'}</Button>
        </div>
      </div>
      <EngineKeyline className="mb-4" />
      {notice && <p className="mb-3 text-xs text-success">{notice}</p>}
      {autosave.recoverable && (
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2 rounded-md border border-ai/30 bg-ai/5 p-2 text-xs">
          <span className="text-text-secondary">Unsaved changes from a previous session were found for this draft.</span>
          <div className="flex gap-2">
            <Button variant="ai" onClick={() => { const st = autosave.restore(); if (st) applyRecovered(st); }}>Restore unsaved changes</Button>
            <Button variant="secondary" onClick={() => autosave.dismissRecovery()}>Discard</Button>
          </div>
        </div>
      )}
      {autosave.conflict && (
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2 rounded-md border border-warning/30 bg-warning/5 p-2 text-xs">
          <span className="text-warning">This draft was updated elsewhere. Reload to get the newer version before editing (your local changes are kept).</span>
          <Button variant="secondary" onClick={() => void itemQ.refetch()}>Reload</Button>
        </div>
      )}
      {autosave.status === 'error' && autosave.errorMessage && (
        <p className="mb-3 text-xs text-danger">{autosave.errorMessage}</p>
      )}

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-[150px_minmax(0,1fr)_320px]">
        <nav className="flex gap-2 overflow-x-auto lg:flex-col lg:overflow-visible" aria-label="Studio steps">
          {STEPS.map((s, i) => (
            <button key={s.key} onClick={() => setStep(s.key)} className={cn('rounded-md px-3 py-2 text-left text-sm', step === s.key ? 'bg-ai/10 font-medium text-ai' : 'text-text-secondary hover:bg-hover')}>
              <span className="mr-1 text-text-tertiary">{i + 1}.</span>{s.label}
            </button>
          ))}
        </nav>

        <div className="space-y-4">
          <Card className="p-4">
            <div className="mb-3 flex items-center justify-between">
              <span className="text-xs text-text-secondary">{channelLabel(item.channel)} · {brief.format === 'carousel' ? `Carousel (${pages.length}/${MAX_CAROUSEL_PAGES})` : 'Single'} · {brief.aspect}</span>
              <span className="text-xs text-text-tertiary">Preview</span>
            </div>
            <div className="mx-auto" style={{ maxWidth: aspectCss(item.channel, brief.aspect) >= 1.5 ? '100%' : '360px' }}>
              <ContentPreview channel={item.channel} aspect={brief.aspect} page={page} brand={brand} workspaceName={title || 'Your brand'} format={brief.format} />
            </div>
          </Card>
          <PublishMirror post={mirrorPost} />
          <PublishControl
            draftId={item.id}
            reviewStatus={item.reviewStatus}
            publishState={item.publishState}
            providerRef={item.providerRef}
            mirror={mirrorPost}
            firstCommentUncertain={linkSources.maybeHiddenFirstComment}
            saveGate={{ status: autosave.status, conflict: autosave.conflict, serialized: editableSerialized, saveNow: autosave.saveNow }}
          />
          {brief.format === 'carousel' && (
            <Card className="p-3">
              <div className="mb-2 flex items-center justify-between">
                <span className="text-xs font-medium text-text-secondary">Pages</span>
                <Button variant="secondary" onClick={() => { if (pages.length >= MAX_CAROUSEL_PAGES) return; setBrief((b) => b ? { ...b, pages: [...b.pages, { idx: b.pages.length, layoutKey: 'carousel-tip', slots: { logo: true }, align: 'start', textSize: 'm' }] } : b); }} disabled={pages.length >= MAX_CAROUSEL_PAGES}>Add page</Button>
              </div>
              <CarouselStrip
                pages={pages}
                active={active}
                onSelect={setActive}
                onMove={(idx, dir) => { const j = idx + dir; if (j < 0 || j >= pages.length) return; setBrief((b) => { if (!b) return b; const next = [...b.pages]; const [m] = next.splice(idx, 1); next.splice(j, 0, m); return { ...b, pages: next.map((p, i) => ({ ...p, idx: i })) }; }); setActive(j); }}
                onRemove={(idx) => { if (pages.length <= 1) return; setBrief((b) => b ? { ...b, pages: b.pages.filter((_, i) => i !== idx).map((p, i) => ({ ...p, idx: i })) } : b); setActive((a) => Math.max(0, Math.min(a, pages.length - 2))); }}
              />
            </Card>
          )}
        </div>

        <Card className="h-fit p-4">
          {step === 'concept' && <ConceptStep channel={item.channel} onApply={(hook) => setSlot('headline', hook)} onBrief={(vb, style) => setBrief((b) => b ? { ...b, brief: vb.visualBrief, imagePrompt: vb.imagePrompt, stylePreset: style } : b)} />}
          {step === 'copy' && <CopyStep channel={item.channel} maxChars={spec?.maxChars} page={page} onApply={(c) => patchPage(active, (p) => ({ ...p, slots: { ...p.slots, headline: c.headline || p.slots.headline, body: c.caption, hashtags: c.hashtags } }))} />}
          {step === 'media' && <StockMediaPicker onSaved={(m, savedId) => { setSlot('media', { assetId: savedId, kind: m.kind, attribution: m.attribution, previewUrl: m.previewUrl }); setNotice('Media added to page ' + (active + 1) + '.'); }} />}
          {step === 'layout' && <LayoutStep brief={brief} page={page} active={active} channel={item.channel} setBrief={setBrief} setSlot={setSlot} />}
          {step === 'preview' && <PreviewStep item={item} brief={brief} maxChars={spec?.maxChars} connectionState={connectionState} pending={doAction.isPending} onAction={(a) => doAction.mutate(a)} />}
        </Card>
      </div>
      <BrandKitPanel open={brandOpen} onClose={() => setBrandOpen(false)} />
    </div>
  );
}

function ConceptStep({ channel, onApply, onBrief }: { channel: Channel; onApply: (hook: string) => void; onBrief: (vb: { visualBrief: string; imagePrompt: string }, style: string) => void }) {
  const [topic, setTopic] = useState('');
  const [goal, setGoal] = useState('');
  const [mood, setMood] = useState('');
  const [style, setStyle] = useState<string>(STYLE_PRESETS[0]);
  const gen = useMutation({ mutationFn: () => studioIdeas({ channel, topic, goal }) });
  const briefGen = useMutation({ mutationFn: () => studioVisualBrief({ channel, topic, mood }) });
  const r = gen.data;
  const vb = briefGen.data;
  return (
    <div className="space-y-3">
      <h3 className="font-display text-sm font-semibold text-text-primary">Campaign concept</h3>
      <input className={inputCls} value={topic} onChange={(e) => setTopic(e.target.value)} placeholder="What is this about?" />
      <input className={inputCls} value={goal} onChange={(e) => setGoal(e.target.value)} placeholder="Goal (e.g. drive bookings)" />
      <Button variant="ai" onClick={() => gen.mutate()} disabled={gen.isPending || topic.trim() === ''}>{gen.isPending ? 'Generating…' : 'Generate ideas'}</Button>
      {gen.isError && <p className="text-sm text-danger">Could not reach the AI service. Try again.</p>}
      {r && r.available === false && <ConnectionRequired message={r.message} />}
      {r && r.available === true && (
        <div className="space-y-2">
          {r.ideas.length > 0 && (
            <div>
              <p className="text-xs font-medium text-text-secondary">Ideas</p>
              <ul className="mt-1 space-y-1">
                {r.ideas.map((i, k) => (
                  <li key={k} className="flex items-start justify-between gap-2 rounded bg-inset px-2 py-1">
                    <span className="text-sm text-text-primary">{i}</span>
                    <button type="button" onClick={() => { setTopic(i); onApply(i); }} className="shrink-0 rounded border border-ai/40 px-2 py-0.5 text-xs font-medium text-ai hover:bg-ai/10">Use</button>
                  </li>
                ))}
              </ul>
              <p className="mt-1 text-[11px] text-text-tertiary">“Use” sets this as the concept &amp; headline and seeds the topic — then Generate caption and Generate visual brief to fill the body, CTA and brief.</p>
            </div>
          )}
          {r.hooks.length > 0 && <div><p className="text-xs font-medium text-text-secondary">Hooks (click to use as headline)</p><div className="mt-1 flex flex-col gap-1">{r.hooks.map((h, k) => <button key={k} onClick={() => onApply(h)} className="rounded border border-border-subtle px-2 py-1 text-left text-sm hover:border-ai">{h}</button>)}</div></div>}
          <Provenance model={r.aiProvenance?.model} version={r.aiProvenance?.prompt_version} />
        </div>
      )}
      <div className="rounded-md border border-border-subtle p-2">
        <p className="text-xs font-medium text-text-secondary">Visual brief</p>
        <input className={inputCls + ' mt-1'} value={mood} onChange={(e) => setMood(e.target.value)} placeholder="Mood (e.g. calm, energetic)" />
        <select className={inputCls + ' mt-1'} value={style} onChange={(e) => setStyle(e.target.value)}>{STYLE_PRESETS.map((sp) => <option key={sp} value={sp}>{sp}</option>)}</select>
        <Button className="mt-2" variant="ai" onClick={() => briefGen.mutate()} disabled={briefGen.isPending || topic.trim() === ''}>{briefGen.isPending ? 'Generating…' : 'Generate visual brief'}</Button>
        {briefGen.isError && <p className="mt-1 text-sm text-danger">Could not reach the AI service. Try again.</p>}
        {vb && vb.available === false && <ConnectionRequired message={vb.message} />}
        {vb && vb.available === true && (
          <div className="mt-2 space-y-1">
            <p className="whitespace-pre-wrap text-sm text-text-secondary">{vb.visualBrief}</p>
            {vb.imagePrompt && <p className="rounded bg-inset px-2 py-1 text-xs text-text-secondary"><span className="font-medium">Design direction (text):</span> {vb.imagePrompt}</p>}
            <p className="text-[11px] text-text-tertiary">Text prompt only — no image is generated (provider owner-gated, G-CSV-3).</p>
            <Button variant="secondary" onClick={() => onBrief({ visualBrief: vb.visualBrief, imagePrompt: vb.imagePrompt }, style)}>Save brief to draft</Button>
          </div>
        )}
      </div>
    </div>
  );
}

function CopyStep({ channel, maxChars, page, onApply }: {
  channel: Channel; maxChars: number | undefined; page: ContentPage;
  onApply: (c: { headline: string; caption: string; hashtags: string[] }) => void;
}) {
  const [topic, setTopic] = useState('');
  const [keyPoints, setKeyPoints] = useState('');
  const [cta, setCta] = useState('');
  const gen = useMutation({ mutationFn: () => studioCaption({ channel, topic, keyPoints, cta }) });
  const r = gen.data;
  const bodyLen = (page.slots.body ?? '').length + (page.slots.headline ?? '').length;
  const over = maxChars ? bodyLen > maxChars : false;
  return (
    <div className="space-y-3">
      <h3 className="font-display text-sm font-semibold text-text-primary">Copy</h3>
      <input className={inputCls} value={topic} onChange={(e) => setTopic(e.target.value)} placeholder="Topic" />
      <textarea className={areaCls} rows={3} value={keyPoints} onChange={(e) => setKeyPoints(e.target.value)} placeholder="Key points (facts to ground the copy — no invented numbers)" />
      <input className={inputCls} value={cta} onChange={(e) => setCta(e.target.value)} placeholder="Call to action (optional)" />
      <Button variant="ai" onClick={() => gen.mutate()} disabled={gen.isPending || topic.trim() === ''}>{gen.isPending ? 'Generating…' : 'Generate caption'}</Button>
      {gen.isError && <p className="text-sm text-danger">Could not reach the AI service. Try again.</p>}
      {r && r.available === false && <ConnectionRequired message={r.message} />}
      {r && r.available === true && (
        <div className="space-y-2 rounded-md border border-border-subtle p-2">
          {r.headline && <p className="text-sm font-semibold text-text-primary">{r.headline}</p>}
          <p className="whitespace-pre-wrap text-sm text-text-secondary">{r.caption}</p>
          {r.violations.length > 0 && <p className="text-xs text-warning">Adjusted for {channel}: {r.violations.join('; ')}</p>}
          {r.flags.length > 0 && <p className="text-xs text-warning">Flags: {r.flags.join('; ')}</p>}
          <Button variant="secondary" onClick={() => onApply({ headline: r.headline, caption: r.caption, hashtags: extractTags(r.caption) })}>Use this copy</Button>
          <Provenance model={r.aiProvenance?.model} version={r.aiProvenance?.prompt_version} />
        </div>
      )}
      <div className="rounded-md bg-inset px-2 py-1 text-xs">
        <span className={over ? 'text-danger' : 'text-text-secondary'}>{bodyLen}{maxChars ? ` / ${maxChars}` : ''} characters{over ? ' — over channel limit' : ''}</span>
      </div>
      <label className="block"><span className="mb-1 block text-xs font-medium text-text-secondary">Headline</span><input className={inputCls} value={page.slots.headline ?? ''} onChange={(e) => onApply({ headline: e.target.value, caption: page.slots.body ?? '', hashtags: page.slots.hashtags ?? [] })} /></label>
      <label className="block"><span className="mb-1 block text-xs font-medium text-text-secondary">Body</span><textarea className={areaCls} rows={4} value={page.slots.body ?? ''} onChange={(e) => onApply({ headline: page.slots.headline ?? '', caption: e.target.value, hashtags: page.slots.hashtags ?? [] })} /></label>
    </div>
  );
}
function extractTags(text: string): string[] {
  return (text.match(/#[\w-]+/g) ?? []).map((t) => t.slice(1)).slice(0, 30);
}

function LayoutStep({ brief, page, active, channel, setBrief, setSlot }: {
  brief: VisualBrief; page: ContentPage; active: number; channel: Channel;
  setBrief: React.Dispatch<React.SetStateAction<VisualBrief | null>>;
  setSlot: <K extends keyof ContentPage['slots']>(k: K, v: ContentPage['slots'][K]) => void;
}) {
  const layouts = CONTENT_LAYOUTS.filter((l) => l.supportedAspects.includes(brief.aspect));
  const [tplName, setTplName] = useState('');
  const saveTpl = useMutation({
    mutationFn: () => createTemplate({ name: tplName, channel: null, layoutKey: brief.pages[0].layoutKey, structure: { layoutKeys: brief.pages.map((p) => p.layoutKey), defaultAspect: brief.aspect, brandBindings: { useLogo: true, fontRole: 'Source Serif 4' } }, bodyTemplate: '' }),
    onSuccess: () => setTplName(''),
  });
  const setPageLayout = (key: string) => setBrief((b) => b ? { ...b, pages: b.pages.map((p, i) => i === active ? { ...p, layoutKey: key } : p) } : b);
  return (
    <div className="space-y-3">
      <h3 className="font-display text-sm font-semibold text-text-primary">Layout</h3>
      <div className="grid grid-cols-1 gap-2">
        {layouts.map((l) => (
          <button key={l.layoutKey} onClick={() => setPageLayout(l.layoutKey)} className={cn('rounded-md border p-2 text-left', page.layoutKey === l.layoutKey ? 'border-ai bg-ai/5' : 'border-border-subtle hover:border-ai')}>
            <p className="text-sm font-medium text-text-primary">{l.name}</p>
            <p className="text-xs text-text-secondary">{l.description}</p>
          </button>
        ))}
      </div>
      <div>
        <p className="mb-1 text-xs font-medium text-text-secondary">Alignment</p>
        <div className="flex gap-1">{(['start', 'center', 'end'] as Align[]).map((a) => <button key={a} onClick={() => setBrief((b) => b ? { ...b, pages: b.pages.map((p, i) => i === active ? { ...p, align: a } : p) } : b)} className={cn('flex-1 rounded border px-2 py-1 text-xs', page.align === a ? 'border-ai bg-ai/10 text-ai' : 'border-border-default text-text-secondary')}>{a}</button>)}</div>
      </div>
      <div>
        <p className="mb-1 text-xs font-medium text-text-secondary">Text size</p>
        <div className="flex gap-1">{(['s', 'm', 'l'] as TextSize[]).map((s) => <button key={s} onClick={() => setBrief((b) => b ? { ...b, pages: b.pages.map((p, i) => i === active ? { ...p, textSize: s } : p) } : b)} className={cn('flex-1 rounded border px-2 py-1 text-xs uppercase', page.textSize === s ? 'border-ai bg-ai/10 text-ai' : 'border-border-default text-text-secondary')}>{s}</button>)}</div>
      </div>
      <div className="flex gap-3 text-xs">
        <label className="flex items-center gap-1 text-text-secondary"><input type="checkbox" checked={!!page.slots.logo} onChange={(e) => setSlot('logo', e.target.checked)} /> Logo</label>
        <label className="flex items-center gap-1 text-text-secondary"><input type="checkbox" checked={!!page.slots.contact} onChange={(e) => setSlot('contact', e.target.checked)} /> Contact</label>
      </div>
      <label className="block"><span className="mb-1 block text-xs font-medium text-text-secondary">CTA text</span><input className={inputCls} value={page.slots.cta ?? ''} onChange={(e) => setSlot('cta', e.target.value)} placeholder="Book now" /></label>
      {isBakedPreview(channel, brief.format) && (
        <div>
          <p className="mb-1 text-xs font-medium text-text-secondary">Overlay opacity <span className="text-text-primary">{page.slots.overlay ?? 45}%</span></p>
          <input type="range" min={0} max={100} step={5} value={page.slots.overlay ?? 45} onChange={(e) => setSlot('overlay', Number(e.target.value))} className="w-full accent-ai" aria-label="Overlay opacity" />
        </div>
      )}
      <div className="rounded-md border border-border-subtle p-2">
        <p className="mb-1 text-xs font-medium text-text-secondary">Save as workspace template</p>
        <div className="flex gap-2"><input className={inputCls} value={tplName} onChange={(e) => setTplName(e.target.value)} placeholder="Template name" /><Button variant="secondary" onClick={() => saveTpl.mutate()} disabled={saveTpl.isPending || tplName.trim() === ''}>Save</Button></div>
        {saveTpl.isError && <p className="mt-1 text-xs text-danger">Could not save template (a name must be unique).</p>}
        {saveTpl.isSuccess && <p className="mt-1 text-xs text-success">Template saved.</p>}
      </div>
    </div>
  );
}

/** Live provider connection state for a channel — 'not_connected' | 'pending' |
 * 'connected' | 'error', or null when the provider directory hasn't loaded /
 * has no entry for the channel (e.g. 'generic'). Sourced from listProviders(). */
export type ConnectionState = Provider['connectionState'];

/** Pure: the calendar-placement footnote shown under an approved item. Honest
 * about REAL connection state (no more "no provider is connected, ever" claim) —
 * fail-closed: only an actually 'connected' provider gets the Publish-capable
 * copy; not_connected/pending/error/unknown(null) all get the same
 * cannot-publish-from-here copy quoting the chip's exact fail-closed label. */
export function scheduleNote(channel: Channel, connectionState: ConnectionState | null): string {
  if (connectionState === 'connected') {
    return `Scheduling places this item on the calendar — nothing publishes automatically. Use Publish (after approval) to post to the connected ${channelLabel(channel)} account.`;
  }
  return `Scheduling places this item on the calendar only. No ${channelLabel(channel)} connection is available yet, so it cannot be published from here — it will read "Scheduled — not auto-published".`;
}

function PreviewStep({ item, brief, maxChars, connectionState, pending, onAction }: {
  item: { reviewStatus: 'draft' | 'in_review' | 'approved' | 'rejected'; scheduledFor: string | null; channel: Channel; publishState: PublishState; providerRef: string | null };
  brief: VisualBrief; maxChars: number | undefined; connectionState: ConnectionState | null; pending: boolean;
  onAction: (a: { kind: 'submit' | 'approve' | 'reject' | 'schedule'; note?: string; when?: string | null }) => void;
}) {
  const [note, setNote] = useState('');
  const [when, setWhen] = useState(item.scheduledFor ? item.scheduledFor.replace(' ', 'T').slice(0, 16) : '');
  const violations = useMemo(() => {
    if (!maxChars) return [] as string[];
    return brief.pages.flatMap((p, i) => {
      const len = (p.slots.headline ?? '').length + (p.slots.body ?? '').length;
      return len > maxChars ? [`Page ${i + 1} exceeds the ${item.channel} limit of ${maxChars} characters (${len}).`] : [];
    });
  }, [brief, maxChars, item.channel]);
  return (
    <div className="space-y-3">
      <h3 className="font-display text-sm font-semibold text-text-primary">Review & schedule</h3>
      {violations.length > 0 ? <div className="rounded-md border border-warning/30 bg-warning/5 p-2 text-xs text-warning">{violations.map((v, i) => <p key={i}>{v}</p>)}</div> : <p className="text-xs text-success">Within channel limits.</p>}

      {(item.reviewStatus === 'draft' || item.reviewStatus === 'rejected') && (
        <Button variant="ai" onClick={() => onAction({ kind: 'submit' })} disabled={pending}>Submit for approval</Button>
      )}
      {item.reviewStatus === 'in_review' && (
        <div className="space-y-2 rounded-md border border-border-subtle p-2">
          <p className="text-xs text-text-secondary">Approval is a human-only action.</p>
          <Button variant="ai" onClick={() => onAction({ kind: 'approve' })} disabled={pending}>Approve</Button>
          <textarea className={areaCls} rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Reason (for changes requested)" />
          <Button variant="secondary" onClick={() => onAction({ kind: 'reject', note })} disabled={pending}>Request changes</Button>
        </div>
      )}
      {item.reviewStatus === 'approved' && (
        <div className="space-y-2 rounded-md border border-border-subtle p-2">
          <div className="flex items-center justify-between">
            <p className="text-xs font-medium text-text-secondary">Calendar placement</p>
            <PublishStateChip publishState={item.publishState} providerRef={item.providerRef} />
          </div>
          <input type="datetime-local" className={inputCls} value={when} onChange={(e) => setWhen(e.target.value)} />
          <div className="flex gap-2">
            <Button variant="ai" onClick={() => onAction({ kind: 'schedule', when: when ? when.replace('T', ' ') + ':00' : null })} disabled={pending || !when}>{item.scheduledFor ? 'Reschedule' : 'Add to calendar'}</Button>
            {item.scheduledFor && <Button variant="secondary" onClick={() => onAction({ kind: 'schedule', when: null })} disabled={pending}>Remove from calendar</Button>}
          </div>
          <p className="text-[11px] text-text-tertiary">{scheduleNote(item.channel, connectionState)}</p>
        </div>
      )}
    </div>
  );
}

function AutosaveIndicator({ status }: { status: AutosaveStatus }) {
  const map: Record<AutosaveStatus, { text: string; cls: string } | null> = {
    idle: null,
    saving: { text: 'Saving…', cls: 'text-text-tertiary' },
    saved: { text: 'All changes saved', cls: 'text-success' },
    error: { text: 'Save failed — kept locally', cls: 'text-danger' },
    conflict: { text: 'Newer version exists', cls: 'text-warning' },
  };
  const s = map[status];
  if (!s) return null;
  return <span className={cn('text-xs', s.cls)} aria-live="polite">{s.text}</span>;
}
function ConnectionRequired({ message }: { message: string }) {
  return (
    <div className="rounded-md border border-warning/30 bg-warning/5 p-3">
      <p className="text-sm font-medium text-warning">AI Studio connection required</p>
      <p className="mt-1 text-xs text-text-secondary">{message}</p>
    </div>
  );
}
function Provenance({ model, version }: { model?: string; version?: string }) {
  // Internal provenance (model id / prompt version) is retained in the saved
  // draft's audit record but is NEVER rendered to the customer. Show nothing.
  void model; void version;
  return null;
}
